import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from '../axios-config/axiosInstance.ts';
import { 
  IVendorReq, 
  IVendorRes, 
  IVendorResponse, 
  ICommonAPIResponse, 
  ICommonListPayloadDto,
  IServiceResponse,
  ISubServiceResponse
} from '../type/type.ts';

interface VendorState {
  vendors: IVendorRes[];
  loading: boolean;
  error: string | null;
  services: any[];
  subServices: any[];
  selectedServiceId: number | null;
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  searchInput: string;
  dataCount: number;
  getVendorByIdValue: { data: IVendorRes } | null;
}

const initialState: VendorState = {
  vendors: [],
  loading: false,
  error: null,
  services: [],
  subServices: [],
  selectedServiceId: null,
  offset: 0,
  limit: 10,
  sortField: 'created_at',
  sortOrder: 1,
  searchInput: '',
  dataCount: 0,
  getVendorByIdValue: null
};

export const fetchServices = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>(
  'vendor/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get<IServiceResponse>('service');
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch services');
    }
  }
);

export const fetchSubServices = createAsyncThunk<
  any[],
  number,
  { rejectValue: string }
>(
  'vendor/fetchSubServices',
  async (serviceId, { rejectWithValue }) => {
    try { 
      const response = await Axios.get<ISubServiceResponse>(`subservice`, {
        params: {
          service_id: serviceId,
          limit: 100 // Get all sub-services for this service
        }
      });
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subservices');
    }
  }
);


export const getAllVendors = createAsyncThunk<
  IVendorResponse,
  ICommonListPayloadDto,
  { rejectValue: string }
>(
  'vendor/getAll',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.get<ICommonAPIResponse>('vendors', { params: payload });
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vendors');
    }
  }
);

export const createVendor = createAsyncThunk<
  IVendorRes,
  IVendorReq,
  { rejectValue: string }
>(
  'vendor/create',
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await Axios.post<IVendorRes>('vendors', vendorData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create vendor');
    }
  }
);

export const updateVendor = createAsyncThunk<
  IVendorRes,
  IVendorReq & { id: number },
  { rejectValue: string }
>(
  'vendor/update',
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await Axios.put<IVendorRes>(`vendors/${vendorData.id}`, vendorData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update vendor');
    }
  }
);

export const deleteVendor = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'vendor/delete',
  async (id, { rejectWithValue }) => {
    try {
      await Axios.delete(`vendors/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete vendor');
    }
  }
);

export const fetchVendor = createAsyncThunk<
  { data: IVendorRes },
  { vendorId: number },
  { rejectValue: string }
>(
  'vendor/fetchVendor',
  async ({ vendorId }, { rejectWithValue }) => {
    try {
      const response = await Axios.get<{
        success: boolean;
        message: string;
        statusCode: number;
        data: IVendorRes;
      }>(`vendors/${vendorId}`);
      
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      
      return { data: response.data.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vendor');
    }
  }
);

const vendorSlice = createSlice({
  name: 'vendorState',
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
    setSelectedServiceId: (state, action) => {
      state.selectedServiceId = action.payload;
      state.subServices = []; // Clear subservices when service changes
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSubServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubServices.fulfilled, (state, action) => {
        state.loading = false;
        state.subServices = action.payload;
      })
      .addCase(fetchSubServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload.data.data;
        state.dataCount = action.payload.data.total;
        state.error = null;
      })
      .addCase(getAllVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVendor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVendor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteVendor.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteVendor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.getVendorByIdValue = action.payload;
      })
      .addCase(fetchVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setOffset,
  setLimit,
  setSortField,
  setSortOrder,
  setSearchInput,
  setSelectedServiceId
} = vendorSlice.actions;

export default vendorSlice.reducer;